import { useContext, useEffect, useMemo, useState } from 'react';

import { Box, Button, Tooltip, useDisclosure } from '@chakra-ui/react';
import { toast } from 'react-toastify';

import {
  File,
  Maybe,
  useDeleteAndReplaceFilesMutation,
  useGenerateRoiDocumentsLazyQuery,
  useMediaQuery,
  useSendRoiDocumentsWithAdobeMutation,
} from '../../../gql/generated/graphql';

import Modal from '../../shared/Modal';
import { parseFilename } from '../utils';

import { groupedMappings } from './MediaTable/hooks/use-media-table-rows';
import RequiredOriginalLesseeModal from './RequiredOriginalLesseeModal';

import { isClosed } from '../../../constants/dealStates';
import { DealActionsEnum, DealContext } from '../../../libs/DealContext';
import { logger } from '../../../libs/Logger';
import { originalLesseeRequiredValidation } from '../../../utils/validation/originalLessee';
import DocumentsTable from '../../DocumentsTable/DocumentsTable';

const SendROIButtonModal = () => {
  const { deal, dispatch, documentMedia } = useContext(DealContext);
  const { internalMediaList, mediaList } = documentMedia;

  const [files, setFiles] = useState<Maybe<File>[]>([]);
  const [selectedFilesIndexes, setSelectedFilesIndexes] = useState<number[]>([]);

  const uploadForSignaturesModal = useDisclosure();
  const requiredOriginalLesseeModal = useDisclosure();

  const hasROIDocuments = useMemo(
    () =>
      internalMediaList.some(
        (media) => media.type && groupedMappings.release_of_interest?.includes(media.type),
      ),
    [internalMediaList],
  );

  const shouldGenerateROIDocuments = useMemo(
    () => deal.financial_info?.buyer_not_lessee && isClosed(deal.state),
    [(deal.financial_info?.buyer_not_lessee, deal.state)],
  );

  const mediaQuery = useMediaQuery({
    skip: !deal.id,
    variables: {
      dealId: deal.id ?? -1,
    },
    fetchPolicy: 'cache-and-network',
  });

  const [generateROIDocuments, { loading: generatingROIDocuments }] =
    useGenerateRoiDocumentsLazyQuery();
  const [sendROIDocumentsWithAdobe, { loading: sendingROIDocuments }] =
    useSendRoiDocumentsWithAdobeMutation();
  const [replaceAndDelete, { loading: replacingAndDeleting }] = useDeleteAndReplaceFilesMutation();
  const isLoading = generatingROIDocuments || sendingROIDocuments || replacingAndDeleting;

  // Select all files by default when the modal is opened.
  useEffect(() => {
    if (!files.length) {
      return;
    }
    setSelectedFilesIndexes(files.map((_, i) => i));
  }, [files]);

  // When closing, always replace and delete the files.
  const handleClose = async () => {
    if (!deal.id) {
      return;
    }

    const newFileNames = files.map((file) => parseFilename(file?.key ?? '').sourceFileName);
    const duplicates = mediaList
      .filter((media) => {
        const existingFileName = parseFilename(media.key ?? '').sourceFileName;
        const isDuplicate = newFileNames.some((file) => file === existingFileName);

        return isDuplicate;
      })
      .map((media) => ({
        key: media.key,
      }));

    const newFileKeys = files.map((file) => ({
      key: file?.key,
    }));

    await replaceAndDelete({
      variables: {
        deal_id: deal.id,
        files_to_delete: duplicates,
        files_to_upload: newFileKeys,
      },
    });
    await mediaQuery.refetch();

    toast.success('ROI documents added/replaced in the media center');

    uploadForSignaturesModal.onClose();
  };

  // Generate and open the upload for signatures modal.
  const handleGenerateROIDocs = async () => {
    if (!deal.id) {
      return;
    }

    requiredOriginalLesseeModal.onClose();

    const internalMedia = internalMediaList.map((media) => media.type);
    const result = await generateROIDocuments({
      errorPolicy: 'ignore',
      fetchPolicy: 'network-only',
      variables: {
        data: {
          deal_id: deal.id,
          internal_media: internalMedia,
        },
      },
    });
    if (
      !result.data?.generateROIDocuments ||
      (!result.data.generateROIDocuments.errors?.length &&
        !result.data.generateROIDocuments.files?.length)
    ) {
      toast.error('Failed to generate ROI documents');
      return;
    }

    const { errors, files: newFiles } = result.data.generateROIDocuments;
    if (errors?.length) {
      errors.map((error) => toast.error(error, { autoClose: false }));
    }
    if (newFiles?.length) {
      setFiles(newFiles);
      uploadForSignaturesModal.onOpen();
    }
  };

  const handleSendForSignatures = async () => {
    if (!deal.id) {
      return;
    }

    try {
      const selectedFileKeys = files
        .filter((_, index) => selectedFilesIndexes.includes(index))
        .map((m) => m?.key);

      const result = await sendROIDocumentsWithAdobe({
        variables: {
          deal_id: deal.id,
          keys: selectedFileKeys,
        },
      });

      if (result.data?.sendROIDocumentsWithAdobe?.roi_document_info) {
        dispatch({
          type: DealActionsEnum.DeepUpdateDeal,
          payload: {
            roi_document_info: result.data.sendROIDocumentsWithAdobe.roi_document_info,
          },
        });
      }

      toast.success('ROI documents sent for signatures');
    } catch (e) {
      logger.error('SendROIButtonModal.tsx', '', null, e);
    } finally {
      handleClose();
    }
  };

  const missingFields = () => {
    if (!deal.original_lessee) {
      return true;
    }

    try {
      originalLesseeRequiredValidation.validateSync(deal.original_lessee);
      return false;
    } catch (error) {
      return true;
    }
  };

  const handleSendROI = async () => {
    if (missingFields()) {
      requiredOriginalLesseeModal.onOpen();
    } else {
      requiredOriginalLesseeModal.onClose();
      await handleGenerateROIDocs();
    }
  };

  if (!shouldGenerateROIDocuments) {
    return null;
  }

  return (
    <>
      <Tooltip
        hasArrow
        label={!hasROIDocuments ? 'No ROI documents required' : ''}
        placement="bottom"
      >
        <Box>
          <Button
            size="sm"
            variant="secondary"
            onClick={handleSendROI}
            isLoading={isLoading}
            loadingText="SEND ROI"
            isDisabled={!hasROIDocuments}
          >
            SEND ROI
          </Button>
        </Box>
      </Tooltip>

      <RequiredOriginalLesseeModal
        {...requiredOriginalLesseeModal}
        onGenerateDocs={handleGenerateROIDocs}
      />

      <Modal
        title="Upload For Signatures"
        isOpen={uploadForSignaturesModal.isOpen}
        onClose={handleClose}
        canDismiss={!isLoading}
        closeOnOverlayClick={false}
        size="3xl"
        leftButtons={
          <Button
            variant="warning"
            onClick={handleClose}
            isLoading={replacingAndDeleting}
            loadingText="CANCEL"
            isDisabled={generatingROIDocuments || sendingROIDocuments}
          >
            CANCEL
          </Button>
        }
        rightButtons={
          <Button
            onClick={handleSendForSignatures}
            isLoading={sendingROIDocuments}
            loadingText="SEND W/ ADOBE SIGN"
            isDisabled={
              !selectedFilesIndexes.length || generatingROIDocuments || replacingAndDeleting
            }
          >
            SEND W/ ADOBE SIGN
          </Button>
        }
      >
        <DocumentsTable
          infoText="Select the documents you would like to upload for digital signing. Regardless of the selection, all of them will be added/replaced in the media center."
          files={files}
          selectedFilesIndexes={selectedFilesIndexes}
          setSelectedFilesIndexes={setSelectedFilesIndexes}
          modal="sign"
        />
      </Modal>
    </>
  );
};

export default SendROIButtonModal;
